import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCalendar, FaUser } from 'react-icons/fa';
import { RiArrowRightUpLine } from 'react-icons/ri';
import './Blog.css';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { BLOG_POSTS } from '../../data/blogPosts';
import LanguageContext from '../../context/LanguageContext.js';

const Blog = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

    const allBlogPosts = BLOG_POSTS.map((post) => ({
        ...post,
        title: t(`blog.post${post.id}.title`),
        excerpt: t(`blog.post${post.id}.excerpt`),
        author: t(`blog.authors.${post.authorRole}`),
    }));

    const filteredPosts =
        selectedCategory === 'all' ? allBlogPosts : allBlogPosts.filter((post) => post.category === selectedCategory);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        if (category === 'all') {
            setSearchParams({});
        } else {
            setSearchParams({ category });
        }
    };

    useEffect(() => {
        const category = searchParams.get('category');
        if (category) {
            setSelectedCategory(category);
        }
    }, [searchParams]);

    const getCategoryLabel = (category) => {
        const categoryMap = {
            all: t('blog.categories.all'),
            recipes: t('blog.categories.recipes'),
            tips: t('blog.categories.tips'),
            news: t('blog.categories.news'),
        };
        return categoryMap[category] || category;
    };

    const layoutDir = dir();

    const categoryFilters = [
        { key: 'all', label: t('blog.categories.all'), count: BLOG_POSTS.length },
        { key: 'recipes', label: t('blog.categories.recipes'), count: BLOG_POSTS.filter((p) => p.category === 'recipes').length },
        { key: 'tips', label: t('blog.categories.tips'), count: BLOG_POSTS.filter((p) => p.category === 'tips').length },
        { key: 'news', label: t('blog.categories.news'), count: BLOG_POSTS.filter((p) => p.category === 'news').length },
    ];

    return (
        <main className="main">
            <Breadcrumb title={t('blog.title')} />

            <section className="blog-root">
                <div className="container blog-header" dir={layoutDir}>
                    <span className="blog-eyebrow">{t('nav.blog')}</span>
                    <p className="blog-page-desc">{t('blog.intro')}</p>
                </div>

                <div className="container blog-layout" dir={layoutDir}>
                    <div className="blog-posts-panel">
                        {filteredPosts.length > 0 ? (
                            filteredPosts.map((post) => (
                                <article className="blog-post-card" key={post.id}>
                                    <Link to={`/${currentLanguage}/blog/${post.id}`} className="blog-post-card-image-link">
                                        <div className="blog-post-image-wrap">
                                            <img src={post.image} alt={post.title} className="blog-post-image" />
                                            <span className="blog-post-category-pill">{getCategoryLabel(post.category)}</span>
                                        </div>
                                    </Link>
                                    <div className="blog-post-card-body">
                                        <div className="blog-post-meta">
                                            <span className="blog-post-meta-item">
                                                <FaCalendar className="blog-post-meta-ico" aria-hidden />
                                                {new Date(post.date).toLocaleDateString(currentLanguage)}
                                            </span>
                                            <span className="blog-post-meta-item">
                                                <FaUser className="blog-post-meta-ico" aria-hidden />
                                                {post.author}
                                            </span>
                                        </div>
                                        <h2 className="blog-post-card-title">
                                            <Link to={`/${currentLanguage}/blog/${post.id}`}>{post.title}</Link>
                                        </h2>
                                        <p className="blog-post-excerpt">{post.excerpt}</p>
                                        <Link to={`/${currentLanguage}/blog/${post.id}`} className="blog-post-read-more">
                                            <span>{t('blog.readMore')}</span>
                                            <RiArrowRightUpLine className="blog-post-read-ico" aria-hidden />
                                        </Link>
                                    </div>
                                </article>
                            ))
                        ) : (
                            <div className="blog-empty">
                                <p>{t('blog.noPosts')}</p>
                            </div>
                        )}
                    </div>

                    <aside className="blog-aside">
                        <div className="blog-aside-inner">
                            <div className="blog-aside-block">
                                <div className="blog-aside-heading">
                                    <span className="blog-aside-heading-text">{t('blog.categories.title')}</span>
                                </div>
                                <ul className="blog-cat-list">
                                    {categoryFilters.map(({ key, label, count }) => (
                                        <li key={key}>
                                            <button
                                                type="button"
                                                className={`blog-cat-btn ${selectedCategory === key ? 'blog-cat-btn--active' : ''}`}
                                                onClick={() => handleCategoryChange(key)}
                                            >
                                                <span>{label}</span>
                                                <span className="blog-cat-count">{count}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="blog-aside-block blog-aside-block--recent">
                                <div className="blog-aside-heading">
                                    <span className="blog-aside-heading-text">{t('blog.recentPosts')}</span>
                                </div>
                                <ul className="blog-recent-list">
                                    {BLOG_POSTS.slice(0, 3).map((post) => (
                                        <li key={post.id}>
                                            <Link to={`/${currentLanguage}/blog/${post.id}`} className="blog-recent-link">
                                                <img src={post.image} alt="" className="blog-recent-thumb" />
                                                <div className="blog-recent-text">
                                                    <span className="blog-recent-title">{t(`blog.post${post.id}.title`)}</span>
                                                    <span className="blog-recent-date">
                                                        {new Date(post.date).toLocaleDateString(currentLanguage)}
                                                    </span>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <blockquote className="blog-aside-quote" aria-hidden="true">
                                {t('blog.quote')}
                            </blockquote>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Blog;
